import { ReactComponent as SuccessIcon } from '@basqet-dashboard/common/assets/icons/success.svg';
import { ReactComponent as AlertIcon } from '@basqet-dashboard/common/assets/icons/warning-static.svg';
import { Button } from '@basqet-dashboard/common/components';
import PropTypes from 'prop-types';
import React from 'react';

import { RESULT_VARIANT } from '../convert-funds.errors';

const SUCCESS_VARIANT = 'success';

const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (value) =>
  safeNumber(value).toLocaleString('en-US', { maximumFractionDigits: 8 });

const formatDateTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const dd = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${dd} ${month} ${yyyy} · ${hh}:${mi}`;
};

const SuccessBody = ({ conversion, quote }) => {
  const fromDisplay = conversion?.from_display
    ? conversion.from_display
    : quote
    ? `${formatNumber(quote.from_amount)} ${quote.from_currency}`
    : '';
  const toDisplay = conversion?.to_display
    ? conversion.to_display
    : quote
    ? `${formatNumber(quote.to_amount)} ${quote.to_currency}`
    : '';
  const conversionId = conversion?.conversion_id;
  const dateLabel =
    formatDateTime(conversion?.created_at) || formatDateTime(new Date().toISOString());
  const exchangeRate = conversion
    ? `1 ${conversion.from_currency} = ${formatNumber(conversion.rate_used)} ${
        conversion.to_currency
      }`
    : quote
    ? `1 ${quote.from_currency} = ${formatNumber(quote.exchange_rate)} ${quote.to_currency}`
    : '';

  return (
    <>
      <span className="convert-step__result-icon convert-step__result-icon--success" aria-hidden>
        <SuccessIcon />
      </span>
      <h2 className="convert-step__big-title">
        Conversion <span className="text-cursive">successful</span>
      </h2>
      <p className="convert-step__success-amount">
        You converted <strong>{fromDisplay}</strong> to
        <em>{toDisplay}</em>
      </p>
      <dl className="convert-step__summary">
        {conversionId && (
          <div className="convert-step__summary-row">
            <dt>Conversion ID</dt>
            <dd>{conversionId}</dd>
          </div>
        )}
        <div className="convert-step__summary-row">
          <dt>Date</dt>
          <dd>{dateLabel}</dd>
        </div>
        {exchangeRate && (
          <div className="convert-step__summary-row">
            <dt>Exchange rate</dt>
            <dd>{exchangeRate}</dd>
          </div>
        )}
      </dl>
    </>
  );
};

SuccessBody.propTypes = {
  conversion: PropTypes.object,
  quote: PropTypes.object
};

SuccessBody.defaultProps = {
  conversion: null,
  quote: null
};

const ErrorBody = ({ title, description }) => (
  <>
    <span className="convert-step__result-icon" aria-hidden>
      <AlertIcon />
    </span>
    <h2 className="convert-step__big-title">{title}</h2>
    <p className="convert-step__muted">{description}</p>
  </>
);

ErrorBody.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

const ResultStep = ({
  variant,
  title,
  description,
  conversion,
  quote,
  onTryAgain,
  onCancel,
  onDone
}) => {
  const isSuccess = variant === SUCCESS_VARIANT;

  return (
    <div className="convert-step convert-step--centered">
      {isSuccess ? (
        <SuccessBody conversion={conversion} quote={quote} />
      ) : (
        <ErrorBody title={title} description={description} />
      )}

      <div className="convert-step__actions">
        {isSuccess ? (
          <>
            <Button variant="outline" onClick={onDone}>
              View conversion details
            </Button>
            <Button onClick={onDone}>Done</Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onTryAgain}>Try again</Button>
          </>
        )}
      </div>
    </div>
  );
};

ResultStep.propTypes = {
  variant: PropTypes.oneOf([...Object.values(RESULT_VARIANT), SUCCESS_VARIANT]).isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  conversion: PropTypes.object,
  quote: PropTypes.object,
  onTryAgain: PropTypes.func,
  onCancel: PropTypes.func,
  onDone: PropTypes.func
};

ResultStep.defaultProps = {
  title: '',
  description: '',
  conversion: null,
  quote: null,
  onTryAgain: () => {},
  onCancel: () => {},
  onDone: () => {}
};

export { ResultStep, SUCCESS_VARIANT };
